import React,{useState,useMemo}from"react";
import{X,Search,UserCheck}from"lucide-react";
import{dispName}from"../config";

const ROLE_COLOR={Admin:{bg:"#EDE9FE",text:"#6D28D9"},Manager:{bg:"#FEF3C7",text:"#92400E"},User:{bg:"#F1F5F9",text:"#475569"}};

function Avatar({email,displayName,size=28}){
  const name=displayName||dispName(email)||"?";
  const initials=name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
  // Deterministic color from email
  const colors=["#1D5FA5","#7C3AED","#059669","#D97706","#DC2626","#0891B2","#9333EA"];
  const idx=(email||"").split("").reduce((a,c)=>a+c.charCodeAt(0),0)%colors.length;
  return(
    <div className="rounded-full flex items-center justify-center shrink-0 font-bold text-white" style={{width:size,height:size,background:colors[idx],fontSize:size*0.38}}>
      {initials}
    </div>
  );
}

export function AssigneeChip({email,displayName,onClear,placeholder="-- Select person --"}){
  const name=displayName||dispName(email)||"";
  if(!email)return(
    <span className="text-sm text-slate-400 flex items-center gap-1.5"><UserCheck size={13}/>{placeholder}</span>
  );
  return(
    <span className="flex items-center gap-2">
      <Avatar email={email} displayName={name} size={22}/>
      <span className="text-sm font-medium text-slate-700 truncate">{name}</span>
      {onClear&&<button type="button" onClick={e=>{e.stopPropagation();onClear();}} className="text-slate-300 hover:text-slate-500 ml-1"><X size={12}/></button>}
    </span>
  );
}

export default function AssigneePicker({users=[],value="",onChange,onClose,required=false,placeholder="-- Select person --"}){
  const[search,setSearch]=useState("");

  const filtered=useMemo(()=>{
    const q=search.toLowerCase();
    if(!q)return users;
    return users.filter(u=>(u.displayName||"").toLowerCase().includes(q)||(u.email||"").toLowerCase().includes(q));
  },[users,search]);

  // Group by role
  const groups=useMemo(()=>{
    const order=["Admin","Manager","User"];
    const g={Admin:[],Manager:[],User:[]};
    filtered.forEach(u=>{const k=order.find(o=>o.toLowerCase()===(u.role||"user").toLowerCase())||"User";g[k].push(u);});
    return order.filter(r=>g[r].length>0).map(r=>({role:r,users:g[r].sort((a,b)=>(a.displayName||"").localeCompare(b.displayName||""))}));
  },[filtered]);

  const select=email=>{onChange(email);onClose();};

  const LABEL={Admin:"Admins",Manager:"Managers",User:"Users"};

  return(
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{background:"rgba(0,0,0,0.5)",zIndex:65}}>
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl flex flex-col" style={{maxHeight:"72vh"}}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 shrink-0">
          <p className="font-bold text-slate-800">Assign To</p>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={17}/></button>
        </div>

        <div className="px-4 py-3 border-b border-slate-100 shrink-0">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input autoFocus value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name…" className="w-full border border-slate-200 rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-blue-300"/>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
          {/* Clear option */}
          {!required&&(
            <button onClick={()=>select("")} className={"w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-colors "+(value===""?"bg-blue-50 border-blue-200":"border-transparent hover:bg-slate-50")}>
              <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center"><UserCheck size={13} className="text-slate-400"/></div>
              <span className="text-sm text-slate-400 italic">{placeholder}</span>
              {value===""&&<span className="ml-auto text-xs font-semibold text-blue-600">Selected</span>}
            </button>
          )}

          {groups.length===0&&<p className="text-sm text-slate-400 text-center py-6">{search?`No people matching "${search}"`:"No users available to assign."}</p>}

          {groups.map(({role,users:rus})=>(
            <div key={role}>
              <div className="flex items-center gap-2 px-2 mb-1.5">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">{LABEL[role]}</p>
                <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full" style={{background:ROLE_COLOR[role]?.bg,color:ROLE_COLOR[role]?.text}}>{rus.length}</span>
              </div>
              <div className="space-y-0.5">
                {rus.map(u=>{
                  const sel=value.toLowerCase()===(u.email||"").toLowerCase();
                  const name=u.displayName||dispName(u.email);
                  return(
                    <button key={u.email} onClick={()=>select(u.email)} className={"w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-colors text-left "+(sel?"bg-blue-50 border-blue-200":"border-transparent hover:bg-slate-50")}>
                      <Avatar email={u.email} displayName={name}/>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-700 truncate">{name}</p>
                        {u.jobTitle&&<p className="text-xs text-slate-400 truncate">{u.jobTitle}</p>}
                      </div>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full shrink-0" style={{background:ROLE_COLOR[role]?.bg,color:ROLE_COLOR[role]?.text}}>{role}</span>
                      {sel&&<span className="text-blue-500 shrink-0">✓</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
